/**
 * useAuthorContentCorpus — real-time subscription to author_posts under
 * a given (author, platform) scope. Phase A.7.
 *
 * Path:
 *   projects/{pid}/authors/{authorId}/platforms/{platformId}/author_posts/*
 *
 * Each author_post is a scraped/manual entry from the author's existing
 * content (the corpus the AI analyses to extract voice markers and
 * post examples).
 */

import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, orderBy, limit as firestoreLimit } from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type { AuthorBasePath } from '../services/AuthorPlatformService';

export interface AuthorPostLite {
  id: string;
  hook?: string;
  body?: string;
  url?: string;
  publishedAt?: any;
  engagement?: {
    likes?: number;
    comments?: number;
    shares?: number;
    saves?: number;
    views?: number;
  };
  source?: 'apify' | 'brightdata' | 'phantombuster' | 'x-api' | 'reddit-api' | 'manual' | 'ai-generated';
  exampleForPostTypes?: string[];
  [key: string]: any;
}

export interface UseAuthorContentCorpusReturn {
  posts: AuthorPostLite[];
  loading: boolean;
  error: Error | null;
}

export function useAuthorContentCorpus(
  projectId: string | null,
  authorId: string | null,
  platformId: string | null,
  pageLimit: number = 50,
  basePath: AuthorBasePath = 'authors',
): UseAuthorContentCorpusReturn {
  const [posts, setPosts] = useState<AuthorPostLite[]>([]);
  const [loading, setLoading] = useState<boolean>(
    Boolean(projectId && authorId && platformId),
  );
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId || !authorId || !platformId) {
      setPosts([]);
      setLoading(false);
      return;
    }
    const db = CoreFirebaseService.getInstance().firestore;
    const q = query(
      collection(
        db,
        'projects', projectId,
        basePath, authorId,
        'platforms', platformId,
        'author_posts',
      ),
      orderBy('publishedAt', 'desc'),
      firestoreLimit(pageLimit),
    );
    setLoading(true);
    const unsub = onSnapshot(
      q,
      (snap) => {
        setPosts(
          snap.docs.map((d) => ({ ...(d.data() as AuthorPostLite), id: d.id })),
        );
        setLoading(false);
      },
      (err) => {
        setError(err as Error);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [projectId, authorId, platformId, pageLimit]);

  return { posts, loading, error };
}
